import { FC } from 'react'

import styled from 'styled-components'

import { Avatar } from 'src/Components/Avatar'
import { CTAButton } from 'src/Components/Buttons/CTAButton'
import { PrimaryDottedButton } from 'src/Components/Buttons/PrimaryDottedButton'
import { DropdownArtists } from 'src/Components/DropdownArtists'
import { PrimaryInput } from 'src/Components/PrimaryInput'
import { addSpaceToNumber } from 'src/Helpers/layoutHelpers'
import { PublicSpotifyArtist } from 'src/Redux/publicSpotifyArtistsApi'
import { MAX_MOBILE_WIDTH, variables } from 'src/Styled/variables'

import { MAX_TRACK_NAME_LENGTH, getIsCreateCampaignEnabled } from './helpers'

const ARTIST_AVATAR_SIZE = 40
const SEARCH_PLACEHOLDER = 'Artist name or Spotify artist URL'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;
  max-width: 960px;

  @media screen and (min-width: ${MAX_MOBILE_WIDTH}) {
    padding: 24px;
    border-radius: 16px;
    border: 1px solid ${({ theme }) => theme.color.lightGrey};
    background: ${({ theme }) => theme.color.white};
    box-shadow: ${({ theme }) => theme.shadow.card};
  }
`

const Title = styled.h3`
  width: 100%;
  margin: 0;
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize18};
  font-style: normal;
  font-weight: ${variables.fontWeight600};
  line-height: 150%;
`

const ArtistDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;

  @media screen and (min-width: ${MAX_MOBILE_WIDTH}) {
    flex-direction: row;
  }
`

const Artist = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  gap: 16px;
  width: 100%;
  min-width: 0;
`

const ArtistInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  line-height: 150%;
`

const ArtistName = styled.span`
  font-weight: ${variables.fontWeight600};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Followers = styled.span`
  font-weight: ${variables.fontWeight400};
`

const CreateError = styled.div`
  color: ${({ theme }) => theme.color.mediumRed};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  text-align: center;
`

const StyledCTAButton = styled(CTAButton)`
  padding-left: 40px;
  padding-right: 40px;
`

export interface SearchArtistSectionProps {
  searchValue: string
  searchedArtists: PublicSpotifyArtist[] | null
  searchError: string
  isSearching: boolean
  selectedArtist: PublicSpotifyArtist | null
  trackName: string
  createError: string
  isCreating: boolean
  onSearchChange: (value: string) => void
  onArtistSelect: (artist: PublicSpotifyArtist) => void
  onChangeArtist: () => void
  onTrackNameChange: (value: string) => void
  onCreateCampaign: () => void
}

export const SearchArtistSection: FC<SearchArtistSectionProps> = ({
  searchValue,
  searchedArtists,
  searchError,
  isSearching,
  selectedArtist,
  trackName,
  createError,
  isCreating,
  onSearchChange,
  onArtistSelect,
  onChangeArtist,
  onTrackNameChange,
  onCreateCampaign,
}) => {
  const artistItems = searchedArtists?.map((artist) => ({
    artistName: artist.name,
    avatarSrc: artist.photoUrl,
    followers: artist.followersCount,
    artistSpotifyId: artist.spotifyId,
    onClick: () => onArtistSelect(artist),
  }))

  return (
    <Container>
      <Title>New Pre-Release Campaign</Title>

      {!selectedArtist && (
        <DropdownArtists
          placeholder={SEARCH_PLACEHOLDER}
          searchValue={searchValue}
          searchedArtists={artistItems}
          isLoading={isSearching}
          error={searchError}
          onSearchChange={onSearchChange}
        />
      )}

      {Boolean(selectedArtist) && (
        <>
          <ArtistDetails>
            <Artist>
              <Avatar
                src={selectedArtist?.photoUrl}
                width={ARTIST_AVATAR_SIZE}
                height={ARTIST_AVATAR_SIZE}
              />
              <ArtistInfo>
                <ArtistName>{selectedArtist?.name}</ArtistName>
                <Followers>
                  {`${addSpaceToNumber(selectedArtist?.followersCount ?? 0, ',')} followers`}
                </Followers>
              </ArtistInfo>
            </Artist>
            <PrimaryDottedButton onClick={onChangeArtist}>
              Change artist
            </PrimaryDottedButton>
          </ArtistDetails>

          <PrimaryInput
            placeholder='Track name'
            value={trackName}
            maxLength={MAX_TRACK_NAME_LENGTH}
            onChange={(e) => onTrackNameChange(e.target.value)}
          />

          {Boolean(createError) && <CreateError>{createError}</CreateError>}

          <StyledCTAButton
            loading={isCreating}
            disabled={!getIsCreateCampaignEnabled(selectedArtist, trackName)}
            onClick={onCreateCampaign}
          >
            Create campaign
          </StyledCTAButton>
        </>
      )}
    </Container>
  )
}
